import { useRef } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import type { Creation } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useSettings } from '../hooks/useSettings.js';
import { NewGameWizard } from '../studio/wizard/NewGameWizard.js';

/** Dedicated creation surface. Subscription authorization remains server-owned. */
export function StudioCreate() {
  const { id } = useParams();
  const { user } = useAuth();
  return <CreationJourney key={`${user?.uid}:${id ?? 'new'}`} id={id} />;
}

function CreationJourney({ id }: { id?: string }) {
  const { user } = useAuth();
  const services = useServices();
  const navigate = useNavigate();
  const { settings, loaded: settingsLoaded, error: settingsError, reload: reloadSettings } = useSettings();
  const creation = useSubscription<(Creation & { id: string }) | null | undefined>(
    (cb) => id ? services.subscribe.creation(id, cb) : () => {},
    undefined,
    [id, user?.uid],
  );

  // This page follows a fresh Create until its first build is over, so the film
  // plays once from the idea to the game. A game that was already live when the
  // page opened belongs to its Studio page.
  const liveAtOpen = useRef<boolean | null>(null);
  if (creation !== undefined && liveAtOpen.current === null) liveAtOpen.current = (creation?.activeVersion ?? 0) > 0;

  if (id && creation === undefined) return <div className="container" role="status">Loading your saved game…</div>;
  if (id && !creation) return (
    <div className="container">
      <p role="alert">This game doesn’t exist or you don’t have access to it.</p>
      <Link to="/studio">Back to Studio</Link>
    </div>
  );
  if (!settingsLoaded) return (
    <div className="container" role="status">Loading creation settings…</div>
  );
  if (settingsError) return (
    <div className="container">
      <p role="alert">We couldn’t load the creation experience. Your game hasn’t been changed.</p>
      <button type="button" className="btn btn--primary" onClick={reloadSettings}>Try again</button>
    </div>
  );
  if (creation && liveAtOpen.current) return <Navigate to={`/studio/g/${creation.id}?tab=iterate`} replace />;

  return (
    <main className="container" style={{ paddingBottom: 48 }}>
      <Link to="/studio">← Your games</Link>
      <h1>{id ? 'Creating your game' : 'Let’s make your game'}</h1>
      <NewGameWizard
        key={`${user?.uid}:${id ?? 'new'}`}
        resume={id ? creation : null}
        creationExperience={settings.creationExperience}
        creationFilmsEnabled={settings.creationFeatures.creationFilms}
        onAnalysisSaved={id ? undefined : (creationId) => navigate(`/studio/create/${creationId}`, { replace: true })}
      />
    </main>
  );
}
